import React, { useCallback, useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Navbar } from '../components/Navbar';
import { AdminLayout } from '../components/AdminSidebar';
import { api, formatApiError } from '../lib/api';
import { toast } from 'sonner';

const STATUS_TABS = [
  { id: 'pending', label: 'Pending review' },
  { id: 'approved', label: 'Approved' },
  { id: 'rejected', label: 'Rejected' },
  { id: 'disabled', label: 'Disabled' },
  { id: 'all', label: 'All' },
];

const ROLE_TABS = [
  { id: 'all', label: 'All partners' },
  { id: 'regional_partner', label: 'Regional Partners' },
  { id: 'memorial_partner', label: 'Memorial Partners' },
];

const BUSINESS_TYPE_LABEL = {
  funeral_home: 'Funeral home',
  cemetery: 'Cemetery',
  memorial_business: 'Memorial business',
  other: 'Other',
};

const ROLE_LABEL = {
  regional_partner: 'Regional Partner',
  memorial_partner: 'Memorial Partner',
};

export default function AdminPartnerApplications() {
  const [sp] = useSearchParams();
  const initialStatus = (() => {
    const v = sp.get('status');
    return ['pending', 'approved', 'rejected', 'disabled', 'all'].includes(v) ? v : 'pending';
  })();
  const [statusFilter, setStatusFilter] = useState(initialStatus);
  const [roleFilter, setRoleFilter] = useState('all');
  const [apps, setApps] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [issuedCode, setIssuedCode] = useState(null);  // {id, code}

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data }, { data: statsData }] = await Promise.all([
        api.get(`/admin/partners/applications?status=${statusFilter}&account_type=${roleFilter}`),
        api.get('/admin/partners/stats').catch(() => ({ data: null })),
      ]);
      setApps(data.applications || []);
      setStats(statsData);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [statusFilter, roleFilter]);

  useEffect(() => { load(); }, [load]);

  const approve = async (app) => {
    if (!window.confirm(`Approve ${app.business_name}? They will be notified by email.`)) return;
    try {
      const { data } = await api.post(`/admin/partners/applications/${app.application_id}/approve`);
      if (data?.partner_invite_code) {
        setIssuedCode({ id: app.application_id, code: data.partner_invite_code });
      }
      toast.success('Partner approved.');
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const reject = async (app) => {
    const reason = window.prompt(`Reject ${app.business_name}? Reason (optional):`, '');
    if (reason === null) return;
    try {
      await api.post(`/admin/partners/applications/${app.application_id}/reject`, { reason });
      toast.success('Application rejected.');
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const disablePartner = async (app) => {
    const reason = window.prompt(`Disable ${app.business_name}? Their records stay intact but commissions will stop accruing. Reason (optional):`, '');
    if (reason === null) return;
    try {
      await api.post(`/admin/partners/${app.user_id}/disable`, { reason });
      toast.success('Partner disabled.');
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const reactivatePartner = async (app) => {
    if (!window.confirm(`Reactivate ${app.business_name}? They'll be able to enroll families again.`)) return;
    try {
      await api.post(`/admin/partners/${app.user_id}/reactivate`);
      toast.success('Partner reactivated.');
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const downloadW9 = async (app) => {
    // iter82 — Friendly UX when the partner hasn't uploaded a W-9 yet.
    // Stripe Connect collects its own copy during onboarding; partners
    // are not required to upload to our internal vault, so it's normal
    // for `w9_uploaded_at` to be null for legitimate accounts.
    if (!app.w9_uploaded_at) {
      toast(
        'No W-9 on file in our internal vault. Stripe Connect collects its own copy during onboarding — check the partner\'s Stripe dashboard for the official copy.',
        { duration: 6000 },
      );
      return;
    }
    try {
      const resp = await api.get(`/admin/partners/${app.user_id}/w9/download`, {
        responseType: 'blob',
      });
      const url = URL.createObjectURL(resp.data);
      window.open(url, '_blank', 'noopener');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      // 404 = no internal copy on file. Make the message human.
      if (e?.response?.status === 404) {
        toast('No W-9 on file in our internal vault yet.');
        return;
      }
      toast.error(formatApiError(e));
    }
  };

  // iter54 — resend the signed Partner Agreement PDF to the partner's
  // email. Useful when the welcome email gets caught in a corporate
  // spam filter or the partner asks for a fresh copy.
  const resendAgreement = async (app) => {
    if (!window.confirm(`Resend the signed Partner Agreement PDF to ${app.email}?`)) return;
    try {
      const { data } = await api.post(`/admin/partners/${app.user_id}/resend-agreement`);
      if (data.delivered) {
        toast.success(`Agreement sent to ${app.email}`);
      } else {
        toast(`Agreement queued (SendGrid not configured locally — PDF still regenerated)`);
      }
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <AdminLayout>
        <header className="mb-8">
          <p className="text-xs uppercase tracking-[0.18em] text-[hsl(var(--accent))]">Platform admin</p>
          <h1 className="font-serif text-4xl text-[hsl(var(--primary))] mt-2" data-testid="admin-partner-apps-title">
            Partner applications
          </h1>
        </header>

        {/* Stats strip */}
        {stats && (
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-8" data-testid="admin-partner-stats">
            <Stat label="Regional · pending"
                  value={stats.regional_partners_pending}
                  to="/admin/partners/applications?status=pending&account_type=regional_partner"
                  testid="admin-stat-regional-pending" />
            <Stat label="Regional · approved"
                  value={stats.regional_partners_approved}
                  to="/admin/partners/applications?status=approved&account_type=regional_partner"
                  testid="admin-stat-regional-approved" />
            <Stat label="Memorial · pending"
                  value={stats.memorial_partners_pending}
                  to="/admin/partners/applications?status=pending&account_type=memorial_partner"
                  testid="admin-stat-memorial-pending" />
            <Stat label="Memorial · approved"
                  value={stats.memorial_partners_approved}
                  to="/admin/partners/applications?status=approved&account_type=memorial_partner"
                  testid="admin-stat-memorial-approved" />
            <Stat label="Families"
                  value={stats.families_total}
                  to="/admin/families"
                  testid="admin-stat-families" />
          </div>
        )}

        {/* Filters */}
        <div className="flex flex-wrap gap-2 mb-3">
          {STATUS_TABS.map((t) => (
            <button
              key={t.id}
              onClick={() => setStatusFilter(t.id)}
              className={`px-4 py-2 rounded-full text-sm border transition ${
                statusFilter === t.id
                  ? 'bg-[hsl(var(--primary))] text-[hsl(var(--accent))] border-[hsl(var(--primary))]'
                  : 'border-[hsl(var(--primary)/0.18)] text-[hsl(var(--primary)/0.8)] hover:bg-[hsl(var(--primary)/0.04)]'
              }`}
              data-testid={`admin-status-tab-${t.id}`}
            >
              {t.label}
            </button>
          ))}
        </div>
        <div className="flex flex-wrap gap-2 mb-8">
          {ROLE_TABS.map((t) => (
            <button
              key={t.id}
              onClick={() => setRoleFilter(t.id)}
              className={`px-4 py-1.5 rounded-full text-xs uppercase tracking-wider border transition ${
                roleFilter === t.id
                  ? 'bg-[hsl(var(--accent))] text-[hsl(var(--primary))] border-[hsl(var(--accent))]'
                  : 'border-[hsl(var(--primary)/0.18)] text-[hsl(var(--primary)/0.7)] hover:bg-[hsl(var(--primary)/0.04)]'
              }`}
              data-testid={`admin-role-tab-${t.id}`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="text-[hsl(var(--primary)/0.6)]">Loading…</div>
        ) : apps.length === 0 ? (
          <div className="rounded-2xl border border-[hsl(var(--primary)/0.18)] px-7 py-10 text-center text-[hsl(var(--primary)/0.6)]" data-testid="admin-partner-apps-empty">
            No applications match this filter.
          </div>
        ) : (
          <div className="space-y-4">
            {apps.map((app) => (
              <div
                key={app.application_id}
                className="rounded-2xl border border-[hsl(var(--primary)/0.18)] bg-[hsl(var(--card))] px-6 py-5"
                data-testid={`admin-partner-app-row-${app.application_id}`}
              >
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div className="min-w-0">
                    <div className="text-xs uppercase tracking-[0.18em] text-[hsl(var(--accent))]">
                      {ROLE_LABEL[app.account_type] || 'Partner'}
                      {app.business_type && (
                        <span className="ml-2 opacity-70">· {BUSINESS_TYPE_LABEL[app.business_type] || app.business_type}</span>
                      )}
                    </div>
                    <h3 className="font-serif text-xl text-[hsl(var(--primary))] mt-1">{app.business_name}</h3>
                    <div className="text-sm text-[hsl(var(--primary)/0.7)] mt-1">
                      {app.name} · {app.email}{app.phone ? ` · ${app.phone}` : ''}
                    </div>
                    {app.legal_business_name && (
                      <div className="text-xs text-[hsl(var(--primary)/0.55)] mt-1">
                        Legal entity: {app.legal_business_name}
                      </div>
                    )}
                    <div className="text-xs text-[hsl(var(--primary)/0.55)] mt-1">
                      Tax ID ····{app.tax_id_last4 || '----'} ·
                      Submitted {(app.submitted_at || '').slice(0, 10)}
                      {app.referred_by_partner_id && (
                        <span> · attached to <code>{app.referred_by_partner_id}</code></span>
                      )}
                    </div>
                  </div>
                  <div className="flex flex-col items-end gap-2">
                    <span className={`text-xs uppercase tracking-wider px-3 py-1 rounded-full ${
                      app.status === 'approved' ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                      : app.status === 'rejected' ? 'bg-red-50 text-red-700 border border-red-200'
                      : 'bg-amber-50 text-amber-700 border border-amber-200'
                    }`}>
                      {app.status}
                    </span>
                    {app.status === 'pending' && (
                      <div className="flex gap-2">
                        <button onClick={() => approve(app)} className="ml-btn-gold" data-testid={`admin-partner-approve-${app.application_id}`}>Approve</button>
                        <button onClick={() => reject(app)} className="ml-btn-secondary text-red-700" data-testid={`admin-partner-reject-${app.application_id}`}>Reject</button>
                      </div>
                    )}
                    {app.status === 'approved' && (
                      <div className="flex gap-2 flex-wrap">
                        <a
                          href={`${process.env.REACT_APP_BACKEND_URL}/api/admin/partners/${app.user_id}/agreement.pdf`}
                          target="_blank"
                          rel="noreferrer"
                          className="ml-btn-secondary"
                          data-testid={`admin-partner-agreement-${app.application_id}`}
                        >
                          Agreement PDF
                        </a>
                        <button onClick={() => resendAgreement(app)} className="ml-btn-secondary" data-testid={`admin-partner-resend-agreement-${app.application_id}`}>
                          Resend agreement
                        </button>
                        <button
                          onClick={() => downloadW9(app)}
                          className={`ml-btn-secondary ${!app.w9_uploaded_at ? 'opacity-60' : ''}`}
                          data-testid={`admin-partner-w9-${app.application_id}`}
                          title={app.w9_uploaded_at
                            ? `Uploaded ${app.w9_uploaded_at.slice(0, 10)}`
                            : 'No W-9 uploaded — Stripe Connect holds the official copy'}
                        >
                          {app.w9_uploaded_at ? 'View W-9' : 'W-9 (none)'}
                        </button>
                        <button onClick={() => disablePartner(app)} className="ml-btn-secondary text-red-700" data-testid={`admin-partner-disable-${app.application_id}`}>Disable</button>
                      </div>
                    )}
                    {app.status === 'disabled' && (
                      <button onClick={() => reactivatePartner(app)} className="ml-btn-gold" data-testid={`admin-partner-reactivate-${app.application_id}`}>Reactivate</button>
                    )}
                    {issuedCode?.id === app.application_id && (
                      <code className="font-mono text-sm bg-[hsl(var(--primary))] text-[hsl(var(--accent))] px-3 py-1 rounded" data-testid={`admin-partner-issued-code-${app.application_id}`}>
                        Invite: {issuedCode.code}
                      </code>
                    )}
                  </div>
                </div>
                {app.rejection_reason && (
                  <div className="mt-3 text-xs text-red-700 bg-red-50 border border-red-100 rounded px-3 py-2">
                    Rejection reason: {app.rejection_reason}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </AdminLayout>
    </div>
  );
}

function Stat({ label, value, to, testid }) {
  const inner = (
    <>
      <div className="text-[10px] uppercase tracking-wider text-[hsl(var(--primary)/0.6)]">{label}</div>
      <div className="font-serif text-2xl text-[hsl(var(--primary))]">{value ?? 0}</div>
    </>
  );
  if (to) {
    return (
      <Link
        to={to}
        data-testid={testid}
        className="rounded-xl border border-[hsl(var(--primary)/0.18)] bg-[hsl(var(--card))] px-4 py-3 hover:border-[hsl(var(--accent))] hover:shadow-[0_8px_24px_-16px_rgba(26,54,38,0.35)] transition-all block"
      >
        {inner}
      </Link>
    );
  }
  return (
    <div data-testid={testid} className="rounded-xl border border-[hsl(var(--primary)/0.18)] bg-[hsl(var(--card))] px-4 py-3">
      {inner}
    </div>
  );
}
